import * as React from 'react'

import { GlobeIcon, SearchIcon } from 'lucide-react'
import type { FlagProps } from 'react-phone-number-input'
import enLabels from 'react-phone-number-input/locale/en.json'
import esLabels from 'react-phone-number-input/locale/es.json'
import frLabels from 'react-phone-number-input/locale/fr.json'
import uaLabels from 'react-phone-number-input/locale/ua.json'
import BasePhoneInput, {
  type Country,
  getCountryCallingCode,
  parsePhoneNumber,
} from 'react-phone-number-input/max'

import { cn } from '@/lib/utils'
import { Button } from '~/client/src/shared/components/shadcn-ui/button'
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  ComboboxTrigger,
  ComboboxValue,
} from '~/client/src/shared/components/shadcn-ui/combobox'
import { Input } from '~/client/src/shared/components/shadcn-ui/input'
import { InputGroupAddon } from '~/client/src/shared/components/shadcn-ui/input-group'
import { ScrollArea } from '~/client/src/shared/components/shadcn-ui/scroll-area'
import Language from '~/client/src/shared/localization/Language'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import { DEFAULT_PHONE_COUNTRY } from '~/client/src/shared/utils/phoneNumberHelpers'
import {
  canAppendPhoneDigit,
  isPhoneNumberValid,
  toE164PhoneNumber,
} from '~/client/src/shared/utils/phoneNumberValidation'

// Composition adapted from ReUI's base-nova phone-input (MIT, keenthemes/reui).

type Flags = typeof import('react-phone-number-input/flags').default

// Every country's flag is ~230 KB of SVG; keep it out of the bundles that render a phone field.
let loadedFlags: Flags = null
let flagsRequest: Promise<Flags> = null

function useFlags(): Flags {
  const [flags, setFlags] = React.useState(loadedFlags)

  React.useEffect(() => {
    if (flags) return
    flagsRequest ??= import('react-phone-number-input/flags').then(
      module => (loadedFlags = module.default),
      () => (flagsRequest = null),
    )
    let isMounted = true
    flagsRequest.then(next => isMounted && next && setFlags(next))
    return () => {
      isMounted = false
    }
  }, [flags])

  return flags
}

const FlagsContext = React.createContext<Flags>(null)

// The library drops the typed digits when another country is picked; keep them under the new
// calling code instead, so a stored number is never cleared by a stray country pick.
function moveToCountry(value: string, country: Country): string {
  const nationalNumber = value && parsePhoneNumber(value)?.nationalNumber
  return nationalNumber && `+${getCountryCallingCode(country)}${nationalNumber}`
}

const COUNTRY_LABELS: Record<Language, Record<string, string>> = {
  [Language.english]: enLabels,
  [Language.spanish]: esLabels,
  [Language.french]: frLabels,
  [Language.ukrainian]: uaLabels,
}

interface IProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'value' | 'onChange' | 'defaultValue'
> {
  // E.164 ('+14155550123'); '' when empty.
  value: string
  onChange: (value: string) => void
  defaultCountry?: Country
}

export function PhoneInput({
  value,
  onChange,
  defaultCountry = DEFAULT_PHONE_COUNTRY,
  className,
  ...inputProps
}: IProps) {
  const flags = useFlags()
  const pickedCountry = React.useRef<Country>(null)
  const phoneNumber = toE164PhoneNumber(value)

  const handleChange = (next?: string) => {
    const country = pickedCountry.current
    pickedCountry.current = null
    onChange((country && moveToCountry(phoneNumber, country)) || next || '')
  }

  return (
    <FlagsContext.Provider value={flags}>
      <BasePhoneInput
        className={cn(
          'tw:flex',
          inputProps['aria-invalid'] &&
            'tw:[&_[data-slot=combobox-trigger]]:border-destructive',
          className,
        )}
        value={phoneNumber || undefined}
        onChange={handleChange}
        defaultCountry={defaultCountry}
        labels={COUNTRY_LABELS[Localization.state.currentLanguage]}
        countryOptionsOrder={[DEFAULT_PHONE_COUNTRY, '...']}
        international
        addInternationalOption={false}
        flagComponent={CountryFlag}
        countrySelectComponent={CountrySelect}
        countrySelectProps={{
          onPick: (country: Country) => {
            pickedCountry.current = country
            setTimeout(() => (pickedCountry.current = null))
          },
        }}
        inputComponent={PhoneNumberInput}
        {...inputProps}
      />
    </FlagsContext.Provider>
  )
}

const PhoneNumberInput = React.forwardRef<
  HTMLInputElement,
  React.InputHTMLAttributes<HTMLInputElement>
>(function PhoneNumberInput(
  { className, onFocus, onBeforeInput, onPaste, ...props },
  ref,
) {
  // Keyboard focus selects the whole value, so the first typed digit would replace the calling
  // code and turn a national number into another country's.
  const moveCaretToEnd = (event: React.FocusEvent<HTMLInputElement>) => {
    onFocus?.(event)
    const input = event.currentTarget
    setTimeout(() => {
      const end = input.value.length
      if (
        document.activeElement === input &&
        end > 0 &&
        input.selectionStart === 0 &&
        input.selectionEnd === end
      ) {
        input.setSelectionRange(end, end)
      }
    })
  }

  // Only a single typed digit at the end is checked; pastes and edits inside the number pass.
  const blockImpossibleDigit = (event: React.FormEvent<HTMLInputElement>) => {
    const { data } = event as React.FormEvent<HTMLInputElement> & {
      data?: string
    }
    const input = event.currentTarget
    const end = input.value.length
    const digits = input.value.replace(/\D/g, '')
    if (
      data?.length === 1 &&
      /\d/.test(data) &&
      digits &&
      input.selectionStart === end &&
      input.selectionEnd === end &&
      !canAppendPhoneDigit(`+${digits}`, data)
    ) {
      event.preventDefault()
      return
    }
    onBeforeInput?.(event)
  }

  // The field always holds a calling code, so a pasted full number would otherwise be appended
  // to it ('+1' + '+44 20…' → '+144 20…'); a number with its own '+' replaces the value instead.
  // A national number pasted over the calling code, or a complete one pasted anywhere, keeps
  // that code and replaces the rest ('+44' + '20 7183 8750' → '+44 20 7183 8750').
  const replaceWithPastedNumber = (
    event: React.ClipboardEvent<HTMLInputElement>,
  ) => {
    onPaste?.(event)
    const text = event.clipboardData?.getData('text').trim() ?? ''
    const input = event.currentTarget
    const [callingCode] = input.value.split(' ')
    const isNationalNumberReplaced =
      callingCode.startsWith('+') &&
      ((input.selectionStart <= callingCode.length &&
        input.selectionEnd === input.value.length) ||
        isPhoneNumberValid(`${callingCode}${text}`))
    if (
      event.defaultPrevented ||
      (!text.startsWith('+') && !isNationalNumberReplaced)
    ) {
      return
    }

    event.preventDefault()
    setInputValue.call(
      input,
      text.startsWith('+') ? text : `${callingCode} ${text}`,
    )
    input.dispatchEvent(new Event('input', { bubbles: true }))
  }

  return (
    <Input
      ref={ref}
      className={cn('tw:rounded-s-none tw:focus-visible:z-10', className)}
      onFocus={moveCaretToEnd}
      onBeforeInput={blockImpossibleDigit}
      onPaste={replaceWithPastedNumber}
      {...props}
    />
  )
})

const setInputValue = Object.getOwnPropertyDescriptor(
  HTMLInputElement.prototype,
  'value',
).set

interface ICountryOption {
  value?: Country
  label: string
}

interface ICountrySelectProps {
  value?: Country
  options: ICountryOption[]
  onChange: (country?: Country) => void
  onPick: (country: Country) => void
  disabled?: boolean
  readOnly?: boolean
  'aria-invalid'?: boolean
}

function CountrySelect({
  value: selectedCountry,
  options,
  onChange,
  onPick,
  disabled,
  readOnly,
}: ICountrySelectProps) {
  const [query, setQuery] = React.useState('')

  const countries = React.useMemo(() => {
    const needle = query.trim().toLowerCase()
    return options.filter(
      ({ value, label }) =>
        value &&
        (label.toLowerCase().includes(needle) ||
          `+${getCountryCallingCode(value)}`.includes(needle)),
    )
  }, [options, query])

  const isDisabled = disabled || readOnly

  return (
    <Combobox
      items={countries}
      filter={null}
      value={selectedCountry || ''}
      onOpenChange={isOpen => !isOpen && setQuery('')}
      onValueChange={(country: Country | null) => {
        if (!country) return
        onPick(country)
        onChange(country)
      }}
    >
      <ComboboxTrigger
        disabled={isDisabled}
        aria-label={Localization.translator.country}
        render={
          <Button
            variant="outline"
            className="tw:gap-1 tw:rounded-e-none tw:border-e-0 tw:px-2.5 tw:focus-visible:z-10 tw:data-pressed:bg-transparent"
          >
            <span className="tw:sr-only">
              <ComboboxValue />
            </span>
            <CountryFlag
              country={selectedCountry}
              countryName={selectedCountry}
            />
          </Button>
        }
      />
      <ComboboxContent className="tw:flex tw:w-xs tw:flex-col tw:gap-2.5 tw:p-2.5">
        <div>
          <ComboboxInput
            placeholder={Localization.translator.search}
            value={query}
            onChange={event => setQuery(event.target.value)}
            showTrigger={false}
          >
            <InputGroupAddon>
              <SearchIcon aria-hidden />
            </InputGroupAddon>
          </ComboboxInput>
        </div>
        <ComboboxEmpty className="tw:text-app-dense">
          {Localization.translator.noResultsFound}
        </ComboboxEmpty>
        <ComboboxList className="tw:p-0">
          {countries.length > 0 && (
            <div className="tw:flex tw:max-h-[inherit] tw:flex-col">
              <ScrollArea className="tw:size-full tw:min-h-0">
                {countries.map(({ value, label }) => (
                  <ComboboxItem
                    key={value}
                    value={value}
                    className="tw:pr-2 tw:text-app-dense tw:data-selected:bg-accent tw:data-selected:text-accent-foreground tw:*:data-[slot=combobox-item-indicator]:hidden"
                  >
                    <CountryFlag country={value} countryName={label} />
                    <span className="tw:flex-1 tw:truncate">{label}</span>
                    <span className="tw:text-muted-foreground">
                      {`+${getCountryCallingCode(value)}`}
                    </span>
                  </ComboboxItem>
                ))}
              </ScrollArea>
            </div>
          )}
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  )
}

function CountryFlag({ country, countryName }: FlagProps) {
  const flags = React.useContext(FlagsContext)
  const Flag = country && flags?.[country]

  return (
    <span className="tw:flex tw:size-4 tw:shrink-0 tw:items-center tw:justify-center tw:overflow-hidden tw:rounded-[3px]">
      {Flag ? (
        <Flag title={countryName} />
      ) : (
        <GlobeIcon className="tw:size-4 tw:text-muted-foreground" />
      )}
    </span>
  )
}
